import { z } from 'zod';

import { isoDateTimeSchema, minorUnitsBigintSchema, ulidSchema } from './primitives';

/** Partition key for platform-wide vouchers on `BonBoca-Restaurants`. */
export const PLATFORM_VOUCHER_PARTITION_KEY = '__PLATFORM__' as const;

export const voucherFundingWireSchema = z.object({
  fromRestaurantPayoutMinorUnits: minorUnitsBigintSchema,
  fromServiceFeeMinorUnits: minorUnitsBigintSchema,
});
export type VoucherFundingWire = z.infer<typeof voucherFundingWireSchema>;

export const voucherEntitySchema = z.object({
  voucherId: ulidSchema,
  /** `restaurantId` or `__PLATFORM__`. */
  partitionKey: z.string().min(1),
  /** Original submitted code (trimmed) for display in backoffice lists. */
  code: z.string().min(1).max(64),
  codeNormalized: z.string().min(1).max(64),
  enabled: z.boolean(),
  discountMinorUnits: minorUnitsBigintSchema,
  minSubtotalMinorUnits: minorUnitsBigintSchema.default(0n),
  maxRedemptions: z.number().int().positive().optional(),
  redemptionCount: z.number().int().nonnegative().default(0),
  expiresAt: isoDateTimeSchema.optional(),
  funding: voucherFundingWireSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type VoucherEntity = z.infer<typeof voucherEntitySchema>;

export const adminCreateVoucherRequestSchema = z
  .object({
    code: z.string().min(2).max(32),
    discountMinorUnits: minorUnitsBigintSchema,
    minSubtotalMinorUnits: minorUnitsBigintSchema.optional(),
    maxRedemptions: z.number().int().positive().optional(),
    expiresAt: isoDateTimeSchema.optional(),
    funding: voucherFundingWireSchema,
  })
  .superRefine((body, ctx) => {
    const sum = body.funding.fromRestaurantPayoutMinorUnits + body.funding.fromServiceFeeMinorUnits;
    if (sum !== body.discountMinorUnits) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'funding.fromRestaurantPayoutMinorUnits + funding.fromServiceFeeMinorUnits must equal discountMinorUnits',
        path: ['funding'],
      });
    }
  });
export type AdminCreateVoucherRequest = z.infer<typeof adminCreateVoucherRequestSchema>;

export const ownerCreateVoucherRequestSchema = adminCreateVoucherRequestSchema;
export type OwnerCreateVoucherRequest = z.infer<typeof ownerCreateVoucherRequestSchema>;

export const createVoucherResponseSchema = z.object({
  voucherId: ulidSchema,
  /** `restaurantId` or `__PLATFORM__`. */
  partitionKey: z.string().min(1),
});
export type CreateVoucherResponse = z.infer<typeof createVoucherResponseSchema>;

export const patchVoucherEnabledRequestSchema = z.object({
  enabled: z.boolean(),
});
export type PatchVoucherEnabledRequest = z.infer<typeof patchVoucherEnabledRequestSchema>;

export const patchVoucherEnabledResponseSchema = z.object({
  voucherId: ulidSchema,
  enabled: z.boolean(),
});
export type PatchVoucherEnabledResponse = z.infer<typeof patchVoucherEnabledResponseSchema>;

export const adminPlatformVoucherPathParamsSchema = z.object({
  voucherId: ulidSchema,
});
export type AdminPlatformVoucherPathParams = z.infer<typeof adminPlatformVoucherPathParamsSchema>;

export const adminRestaurantVoucherPathParamsSchema = z.object({
  id: ulidSchema,
  voucherId: ulidSchema,
});
export type AdminRestaurantVoucherPathParams = z.infer<
  typeof adminRestaurantVoucherPathParamsSchema
>;

export const voucherListItemSchema = z.object({
  voucherId: ulidSchema,
  code: z.string(),
  enabled: z.boolean(),
  discountMinorUnits: minorUnitsBigintSchema,
  minSubtotalMinorUnits: minorUnitsBigintSchema,
  maxRedemptions: z.number().int().positive().optional(),
  redemptionCount: z.number().int().nonnegative(),
  expiresAt: isoDateTimeSchema.optional(),
  funding: voucherFundingWireSchema,
});
export type VoucherListItem = z.infer<typeof voucherListItemSchema>;

export const listVouchersResponseSchema = z.object({
  items: z.array(voucherListItemSchema),
});
export type ListVouchersResponse = z.infer<typeof listVouchersResponseSchema>;
